import { twMerge } from 'tailwind-merge';

/**
 * Stable hash → index for mapping org unit ids to a fixed palette.
 * Palette follows common PM / enterprise patterns: distinct, muted fills, strong left rail.
 */
function hashOrgId(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i += 1) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Row: left accent + subtle background. Chip: compact label. Button: card border + tint (department picker). */
type DeptTone = {
  row: string;
  chip: string;
  button: string;
  dot: string;
};

/**
 * Twelve distinguishable hues (WCAG-friendly contrast on white; left rail ~600–700).
 * Order is fixed so the same org id always maps to the same colour.
 */
const PALETTE: readonly DeptTone[] = [
  {
    row: 'border-l-[4px] border-l-slate-600 bg-gradient-to-r from-slate-100/90 via-slate-50/50 to-white',
    chip: 'border border-slate-200 bg-slate-100/95 text-slate-800 ring-1 ring-slate-300/40',
    button: 'border-slate-300 bg-slate-50/90 hover:border-slate-400 hover:bg-slate-100/90',
    dot: 'bg-slate-600',
  },
  {
    row: 'border-l-[4px] border-l-blue-700 bg-gradient-to-r from-blue-50/95 via-blue-50/40 to-white',
    chip: 'border border-blue-200/90 bg-blue-50 text-blue-900 ring-1 ring-blue-200/60',
    button: 'border-blue-200 bg-blue-50/90 hover:border-blue-400 hover:bg-blue-100/80',
    dot: 'bg-blue-700',
  },
  {
    row: 'border-l-[4px] border-l-indigo-700 bg-gradient-to-r from-indigo-50/95 via-indigo-50/35 to-white',
    chip: 'border border-indigo-200/90 bg-indigo-50 text-indigo-900 ring-1 ring-indigo-200/60',
    button: 'border-indigo-200 bg-indigo-50/90 hover:border-indigo-400 hover:bg-indigo-100/80',
    dot: 'bg-indigo-700',
  },
  {
    row: 'border-l-[4px] border-l-violet-700 bg-gradient-to-r from-violet-50/95 via-violet-50/35 to-white',
    chip: 'border border-violet-200/90 bg-violet-50 text-violet-900 ring-1 ring-violet-200/60',
    button: 'border-violet-200 bg-violet-50/90 hover:border-violet-400 hover:bg-violet-100/80',
    dot: 'bg-violet-700',
  },
  {
    row: 'border-l-[4px] border-l-cyan-700 bg-gradient-to-r from-cyan-50/95 via-cyan-50/35 to-white',
    chip: 'border border-cyan-200/90 bg-cyan-50 text-cyan-950 ring-1 ring-cyan-200/60',
    button: 'border-cyan-200 bg-cyan-50/90 hover:border-cyan-400 hover:bg-cyan-100/80',
    dot: 'bg-cyan-700',
  },
  {
    row: 'border-l-[4px] border-l-teal-700 bg-gradient-to-r from-teal-50/95 via-teal-50/35 to-white',
    chip: 'border border-teal-200/90 bg-teal-50 text-teal-950 ring-1 ring-teal-200/60',
    button: 'border-teal-200 bg-teal-50/90 hover:border-teal-500 hover:bg-teal-100/80',
    dot: 'bg-teal-700',
  },
  {
    row: 'border-l-[4px] border-l-emerald-700 bg-gradient-to-r from-emerald-50/95 via-emerald-50/35 to-white',
    chip: 'border border-emerald-200/90 bg-emerald-50 text-emerald-950 ring-1 ring-emerald-200/60',
    button: 'border-emerald-200 bg-emerald-50/90 hover:border-emerald-500 hover:bg-emerald-100/80',
    dot: 'bg-emerald-700',
  },
  {
    row: 'border-l-[4px] border-l-amber-700 bg-gradient-to-r from-amber-50/95 via-amber-50/40 to-white',
    chip: 'border border-amber-200/90 bg-amber-50 text-amber-950 ring-1 ring-amber-200/60',
    button: 'border-amber-200 bg-amber-50/90 hover:border-amber-500 hover:bg-amber-100/80',
    dot: 'bg-amber-700',
  },
  {
    row: 'border-l-[4px] border-l-orange-700 bg-gradient-to-r from-orange-50/95 via-orange-50/35 to-white',
    chip: 'border border-orange-200/90 bg-orange-50 text-orange-950 ring-1 ring-orange-200/60',
    button: 'border-orange-200 bg-orange-50/90 hover:border-orange-400 hover:bg-orange-100/80',
    dot: 'bg-orange-700',
  },
  {
    row: 'border-l-[4px] border-l-rose-700 bg-gradient-to-r from-rose-50/95 via-rose-50/35 to-white',
    chip: 'border border-rose-200/90 bg-rose-50 text-rose-950 ring-1 ring-rose-200/60',
    button: 'border-rose-200 bg-rose-50/90 hover:border-rose-400 hover:bg-rose-100/80',
    dot: 'bg-rose-700',
  },
  {
    row: 'border-l-[4px] border-l-sky-700 bg-gradient-to-r from-sky-50/95 via-sky-50/35 to-white',
    chip: 'border border-sky-200/90 bg-sky-50 text-sky-950 ring-1 ring-sky-200/60',
    button: 'border-sky-200 bg-sky-50/90 hover:border-sky-400 hover:bg-sky-100/80',
    dot: 'bg-sky-700',
  },
  {
    row: 'border-l-[4px] border-l-stone-600 bg-gradient-to-r from-stone-100/90 via-stone-50/50 to-white',
    chip: 'border border-stone-200 bg-stone-100/95 text-stone-900 ring-1 ring-stone-300/50',
    button: 'border-stone-300 bg-stone-50/90 hover:border-stone-400 hover:bg-stone-100/90',
    dot: 'bg-stone-600',
  },
];

const UNASSIGNED: DeptTone = {
  row: 'border-l-[3px] border-l-slate-300 bg-gradient-to-r from-white via-slate-50/30 to-white',
  chip: 'border border-dashed border-slate-200 bg-white text-slate-500',
  button: 'border-slate-200 bg-white/80 hover:border-slate-300 hover:bg-slate-50/80',
  dot: 'bg-slate-400',
};

export function departmentToneForOrgId(orgId: string | undefined): DeptTone {
  if (!orgId || !orgId.trim()) return UNASSIGNED;
  const idx = hashOrgId(orgId) % PALETTE.length;
  return PALETTE[idx] ?? PALETTE[0]!;
}

export function getDepartmentAssignRowClasses(
  orgId: string | undefined,
  opts: { selected: boolean }
): string {
  const tone = departmentToneForOrgId(orgId);
  const interactive = true;
  return twMerge(
    'border-b border-slate-100/80 transition-colors last:border-b-0',
    interactive ? 'cursor-pointer' : '',
    tone.row,
    opts.selected
      ? 'z-[1] ring-2 ring-inset ring-teal-600/50 shadow-[inset_0_0_0_1px_rgba(13,148,136,0.18)]'
      : '',
    !opts.selected && interactive ? 'hover:brightness-[1.015]' : ''
  );
}

export function getDepartmentChipClasses(orgId: string | undefined): string {
  const tone = departmentToneForOrgId(orgId);
  return twMerge('inline-flex max-w-full items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium', tone.chip);
}

export function getDepartmentCardButtonClasses(orgId: string, disabled: boolean): string {
  const tone = departmentToneForOrgId(orgId);
  return twMerge(
    'w-full rounded-xl border px-3 py-3 text-left transition',
    disabled ? 'cursor-not-allowed border-slate-200 bg-white/60 opacity-60' : twMerge('shadow-sm', tone.button)
  );
}

export function departmentDotClasses(orgId: string): string {
  const tone = departmentToneForOrgId(orgId);
  return twMerge('h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white', tone.dot);
}
